import { Component, computed, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Textarea } from 'primeng/textarea';
import { Fluid } from 'primeng/fluid';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { RealmService } from '@/app/services/realm.service';
import { RealmConfig, RealmFineraConfig, RealmFineraWithdrawConfig } from '@/lib-backend';
import { isSkillSpecific } from '@/app/utils/inheritance';

export function emptyFineraWithdraw(): RealmFineraWithdrawConfig {
    return { GroupId: '', ControlKey: '', ConnectingPartyName: '', RsaPrivateKeyPem: '' };
}

@Component({
    selector: 'app-website-info-finera',
    standalone: true,
    imports: [
        FormsModule, InputText, Checkbox, Textarea, Fluid,
        InputGroup, InputGroupAddon, Button, Tooltip, Dialog, Select,
    ],
    templateUrl: './finera-tab.html',
})
export class FineraTab {
    readonly realm = input.required<RealmConfig>();
    readonly draft = model.required<RealmFineraConfig>();
    /** Realm-owned Finera withdraw credentials; only meaningful for skill-based realms. */
    readonly withdrawDraft = model.required<RealmFineraWithdrawConfig>();

    protected readonly realmService = inject(RealmService);

    protected readonly isSkill = computed(() => isSkillSpecific(this.realm().CategorySpecific));

    /** Baseline Finera withdraw credentials from the realm (skill only). */
    protected readonly baseWithdraw = computed<RealmFineraWithdrawConfig>(() => {
        const cs = this.realm().CategorySpecific;
        return isSkillSpecific(cs) ? cs.FineraWithdraw : emptyFineraWithdraw();
    });

    protected readonly dirty = computed(() => {
        const fineraDirty = JSON.stringify(this.draft()) !== JSON.stringify(this.realm().Finera);
        const withdrawDirty = this.isSkill() &&
            JSON.stringify(this.withdrawDraft()) !== JSON.stringify(this.baseWithdraw());
        return fineraDirty || withdrawDirty;
    });

    protected readonly importableRealms = computed(() =>
        this.realmService.realms().filter(r => r.RealmId !== this.realm().RealmId)
    );

    protected importDialogVisible = signal(false);
    protected importSourceRealmId = signal<string | null>(null);
    protected importLoading = signal(false);

    protected setField<K extends keyof RealmFineraConfig>(field: K, value: RealmFineraConfig[K]): void {
        this.draft.update(d => ({ ...d, [field]: value }));
    }

    protected setDeposit(field: keyof RealmFineraConfig['Deposit'], value: string): void {
        this.draft.update(d => ({ ...d, Deposit: { ...d.Deposit, [field]: value } }));
    }

    protected setWithdraw(field: keyof RealmFineraWithdrawConfig, value: string): void {
        this.withdrawDraft.update(d => ({ ...d, [field]: value }));
    }

    protected resetAll(): void {
        const f = this.realm().Finera;
        this.draft.set({ ...f, Deposit: { ...f.Deposit } });
        this.withdrawDraft.set({ ...this.baseWithdraw() });
    }

    protected openImportDialog(): void {
        this.realmService.loadRealms();
        this.importSourceRealmId.set(null);
        this.importDialogVisible.set(true);
    }

    protected async applyImport(): Promise<void> {
        const realmId = this.importSourceRealmId();
        if (!realmId) return;
        this.importLoading.set(true);
        try {
            const result = await this.realmService.getRealm(realmId);
            if (result) {
                const cfg = result.config;
                this.draft.set({ ...cfg.Finera, Deposit: { ...cfg.Finera.Deposit } });
                const cs = cfg.CategorySpecific;
                if (this.isSkill() && isSkillSpecific(cs)) {
                    this.withdrawDraft.set({ ...cs.FineraWithdraw });
                }
            }
        } finally {
            this.importLoading.set(false);
        }
        this.importDialogVisible.set(false);
    }
}
